import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { LogOut, ShieldAlert, ShieldCheck, Trash2, ChevronLeft, Search, Users, Phone, IndianRupee, Package2 } from 'lucide-react';
import Modal from '../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;
const ic = "w-full rounded-md border border-[#E4DFD3] bg-white px-3 py-2 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]";

const Master = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDisabled, setShowDisabled] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [partyDetail, setPartyDetail] = useState(null); // {loading, party, sales}

  const openParty = async (partyId) => {
    setPartyDetail({ loading: true });
    try {
      const { data } = await axios.get(`${API}/api/superadmin/parties/${partyId}`, { withCredentials: true });
      setPartyDetail({ loading: false, party: data.party, sales: data.sales });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load party');
      setPartyDetail(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/superadmin/users`, { withCredentials: true });
      setUsers(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openUser = async (uid) => {
    setDetailLoading(true);
    setDetail({ id: uid });
    try {
      const { data } = await axios.get(`${API}/api/superadmin/users/${uid}`, { withCredentials: true });
      setDetail(data);
    } catch (err) {
      toast.error('Failed to load user');
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const toggleDisable = async (u) => {
    if (!window.confirm(`${u.disabled ? 'Re-enable' : 'Temporarily disable'} ${u.name} (${u.email})?`)) return;
    try {
      await axios.put(`${API}/api/superadmin/users/${u.id}/disable`, { disabled: !u.disabled }, { withCredentials: true });
      toast.success(u.disabled ? 'User re-enabled' : 'User disabled (cannot login)');
      fetchUsers();
      if (detail?.profile?.id === u.id) openUser(u.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const permaDelete = async (u) => {
    try {
      await axios.delete(`${API}/api/superadmin/users/${u.id}`, { withCredentials: true });
      toast.success('User permanently deleted');
      setConfirmDelete(null);
      setDetail(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const markVerified = async (u) => {
    if (!window.confirm(`Mark ${u.name} (${u.email}) as email-verified? They will be able to log in immediately.`)) return;
    try {
      await axios.put(`${API}/api/superadmin/users/${u.id}/verify`, {}, { withCredentials: true });
      toast.success('User marked as verified');
      fetchUsers();
      if (detail?.profile?.id === u.id) openUser(u.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const filtered = users.filter(u => {
    if (!showDisabled && u.disabled) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [u.name, u.email, u.phone, u.role].some(v => (v || '').toLowerCase().includes(q));
  });

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F7F5F0]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C4A3B]"></div></div>;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="master-page">
      {/* Header */}
      <header className="bg-[#1A2620] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3E6652] rounded-lg flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold font-['Manrope']">Master Console</h1>
              <p className="text-xs text-white/70">Platform owner — {user.email}</p>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-md text-sm" data-testid="master-logout"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {detail ? (
          <UserDetail
            detail={detail}
            loading={detailLoading}
            onBack={() => setDetail(null)}
            onToggleDisable={() => toggleDisable(detail.profile)}
            onPermaDelete={() => setConfirmDelete(detail.profile)}
            onMarkVerified={() => markVerified(detail.profile)}
            onPartyClick={openParty}
          />
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Total Users" value={users.length} testId="stat-total" />
              <StatCard label="Admins" value={users.filter(u => u.role === 'admin').length} testId="stat-admins" />
              <StatCard label="Staff" value={users.filter(u => u.role === 'staff').length} testId="stat-staff" />
              <StatCard label="Unverified" value={users.filter(u => !u.email_verified && !u.disabled).length} testId="stat-unverified" tone="amber" />
              <StatCard label="Disabled" value={users.filter(u => u.disabled).length} testId="stat-disabled" tone="red" />
            </div>

            {/* Search bar */}
            <div className="bg-white border border-[#E4DFD3] rounded-lg p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B63]" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone, role…" className={`${ic} pl-9`} data-testid="master-search" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#1A2620]">
                <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} className="rounded border-[#E4DFD3] text-[#2C4A3B] focus:ring-[#2C4A3B]" />
                Show disabled
              </label>
            </div>

            {/* Users table */}
            <div className="bg-white border border-[#E4DFD3] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[#E4DFD3] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#2C4A3B]" />
                <h2 className="font-bold text-[#1A2620]">All Users ({filtered.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="bg-[#F7F5F0] border-b border-[#E4DFD3] text-left text-xs font-semibold uppercase tracking-wider text-[#5C6B63]">
                    <th className="p-3">Name</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Phone</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">Workspace</th>
                    <th className="p-3">Joined</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr></thead>
                  <tbody data-testid="master-users-list">
                    {filtered.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-sm text-[#5C6B63]">No users match.</td></tr> : filtered.map(u => (
                      <tr key={u.id} className={`border-b border-[#E4DFD3] last:border-0 hover:bg-[#FDFCFB] cursor-pointer ${u.disabled ? 'opacity-60' : ''}`} data-testid={`master-user-${u.id}`}>
                        <td className="p-3 text-sm font-medium text-[#1A2620] cursor-pointer" onClick={() => openUser(u.id)}>{u.name}</td>
                        <td className="p-3 text-sm text-[#1A2620]" onClick={() => openUser(u.id)}>{u.email}</td>
                        <td className="p-3 text-sm text-[#1A2620]">{u.phone || '—'}</td>
                        <td className="p-3 text-sm">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${u.role === 'superadmin' ? 'bg-[#1A2620] text-white' : u.role === 'admin' ? 'bg-[#2C4A3B] text-white' : 'bg-[#EDF5EE] text-[#3A7D44]'}`}>{u.role}</span>
                        </td>
                        <td className="p-3 text-xs text-[#5C6B63] font-mono truncate max-w-[120px]" title={u.workspace_id}>{u.workspace_id?.slice(0, 8) || '—'}</td>
                        <td className="p-3 text-sm text-[#5C6B63]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                        <td className="p-3 text-sm">{u.disabled ? <span className="text-[#D05A4A] font-semibold" data-testid={`status-disabled-${u.id}`}>Disabled</span> : u.email_verified ? <span className="text-[#3A7D44]" data-testid={`status-active-${u.id}`}>Active</span> : <span className="text-[#8A6A00] font-semibold" data-testid={`status-unverified-${u.id}`} title="User has not verified their email yet">Unverified</span>}</td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end">
                            {u.role !== 'superadmin' && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); toggleDisable(u); }} className={`p-1.5 rounded ${u.disabled ? 'hover:bg-[#EDF5EE] text-[#3A7D44]' : 'hover:bg-[#FFF7E5] text-[#8A6A00]'}`} data-testid={`toggle-${u.id}`} title={u.disabled ? 'Re-enable' : 'Disable temporarily'}>
                                  {u.disabled ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(u); }} className="p-1.5 hover:bg-[#FAEDED] rounded text-[#D05A4A]" data-testid={`delete-${u.id}`} title="Permanently delete"><Trash2 className="w-4 h-4" /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal show={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Permanently delete?" testId="delete-confirm">
        {confirmDelete && (
          <div className="space-y-4">
            <p className="text-sm text-[#1A2620]">This will <strong>permanently delete</strong> <span className="font-semibold">{confirmDelete.name}</span> ({confirmDelete.email}) from the platform. Their workspace data (bags, sales, etc.) will remain for auditing but they will no longer be able to log in.</p>
            <p className="text-xs text-[#5C6B63]">If you only want to block them temporarily, use the disable button instead.</p>
            <div className="flex gap-2 pt-2">
              <button onClick={() => permaDelete(confirmDelete)} className="flex-1 bg-[#D05A4A] hover:bg-[#B5483A] text-white rounded-md px-4 py-2 font-medium" data-testid="confirm-delete-btn">Yes, delete permanently</button>
              <button onClick={() => setConfirmDelete(null)} className="flex-1 border border-[#E4DFD3] text-[#2C4A3B] rounded-md px-4 py-2 font-medium">Cancel</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal show={!!partyDetail} onClose={() => setPartyDetail(null)} title="Party Details" testId="master-party-modal">
        {partyDetail?.loading ? (
          <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4A3B]"></div></div>
        ) : partyDetail?.party && (
          <div className="space-y-4" data-testid="master-party-detail">
            <div className="border-b border-[#E4DFD3] pb-3">
              <h3 className="text-xl font-bold text-[#1A2620] font-['Manrope']" data-testid="party-detail-name">{partyDetail.party.name}</h3>
              <p className="text-xs text-[#5C6B63] mt-0.5">Registered {partyDetail.party.created_at ? new Date(partyDetail.party.created_at).toLocaleDateString() : '—'} by {partyDetail.party.logged_by || '—'}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 p-3 bg-[#F7F5F0] rounded-lg">
                <Phone className="w-4 h-4 text-[#2C4A3B] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Phone</p>
                  <p className="text-sm font-medium text-[#1A2620] break-all" data-testid="party-detail-phone">{partyDetail.party.phone || '—'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-[#F7F5F0] rounded-lg">
                <Package2 className="w-4 h-4 text-[#2C4A3B] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Qty / Packet</p>
                  <p className="text-sm font-medium text-[#1A2620]">{partyDetail.party.quantity_per_packet ?? '—'} kg</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-[#F7F5F0] rounded-lg">
                <IndianRupee className="w-4 h-4 text-[#2C4A3B] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Price / Packet</p>
                  <p className="text-sm font-medium text-[#1A2620]">Rs.{Number(partyDetail.party.price_per_packet || 0).toFixed(0)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-[#F7F5F0] rounded-lg">
                <Users className="w-4 h-4 text-[#2C4A3B] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Payment Method</p>
                  <p className="text-sm font-medium text-[#1A2620] capitalize">{partyDetail.party.payment_method || '—'}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-[#EDF5EE] rounded-lg">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Total Purchase</p>
                <p className="text-lg font-bold text-[#3A7D44]">Rs.{Number(partyDetail.party.total_purchase || 0).toFixed(0)}</p>
              </div>
              <div className="p-3 bg-[#EDF5EE] rounded-lg">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Total Paid</p>
                <p className="text-lg font-bold text-[#3A7D44]">Rs.{Number(partyDetail.party.total_paid || 0).toFixed(0)}</p>
              </div>
              <div className="p-3 bg-[#FAEDED] rounded-lg">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">Pending</p>
                <p className="text-lg font-bold text-[#D05A4A]">Rs.{Number(partyDetail.party.total_pending || 0).toFixed(0)}</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#1A2620] mb-2">Recent Sales ({partyDetail.sales?.length || 0})</p>
              {partyDetail.sales?.length === 0 ? (
                <p className="text-xs text-[#5C6B63] py-3 text-center bg-[#F7F5F0] rounded">No sales recorded yet.</p>
              ) : (
                <div className="border border-[#E4DFD3] rounded-lg max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#F7F5F0]"><tr className="text-left text-[10px] uppercase font-semibold text-[#5C6B63]">
                      <th className="p-2">Date</th><th className="p-2">Packets</th><th className="p-2">Total</th><th className="p-2">Pay</th>
                    </tr></thead>
                    <tbody>
                      {partyDetail.sales.slice(0, 30).map((s, i) => (
                        <tr key={s.id || i} className="border-t border-[#E4DFD3]">
                          <td className="p-2 text-[#1A2620]">{s.sale_date ? new Date(s.sale_date).toLocaleDateString() : '—'}</td>
                          <td className="p-2 text-[#1A2620]">{s.num_packets}</td>
                          <td className="p-2 text-[#1A2620] font-medium">Rs.{Number(s.total_amount || 0).toFixed(0)}</td>
                          <td className="p-2 text-[#5C6B63] capitalize">{s.payment_type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const StatCard = ({ label, value, tone, testId }) => {
  const bg = tone === 'red' ? 'bg-[#FAEDED]' : tone === 'amber' ? 'bg-[#FFF7E5]' : '';
  const fg = tone === 'red' ? 'text-[#D05A4A]' : tone === 'amber' ? 'text-[#8A6A00]' : 'text-[#1A2620]';
  return (
    <div className={`bg-white border border-[#E4DFD3] rounded-lg p-4 ${bg}`} data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B63] mb-1">{label}</p>
      <p className={`text-2xl font-bold font-['IBM_Plex_Sans'] ${fg}`}>{value}</p>
    </div>
  );
};

const UserDetail = ({ detail, loading, onBack, onToggleDisable, onPermaDelete, onMarkVerified, onPartyClick }) => {
  if (loading || !detail?.profile) return (
    <div className="bg-white border border-[#E4DFD3] rounded-lg p-8 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2C4A3B]"></div>
    </div>
  );
  const p = detail.profile;
  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-[#2C4A3B] hover:underline flex items-center gap-1" data-testid="master-back-btn"><ChevronLeft className="w-4 h-4" /> Back to all users</button>

      <div className="bg-white border border-[#E4DFD3] rounded-lg p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h2 className="text-2xl font-bold text-[#1A2620] font-['Manrope']">{p.name}</h2>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${p.role === 'superadmin' ? 'bg-[#1A2620] text-white' : p.role === 'admin' ? 'bg-[#2C4A3B] text-white' : 'bg-[#EDF5EE] text-[#3A7D44]'}`}>{p.role}</span>
              {p.disabled && <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-[#FAEDED] text-[#D05A4A]">Disabled</span>}
              {!p.email_verified && !p.disabled && p.role !== 'superadmin' && <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-[#FFF7E5] text-[#8A6A00]" data-testid="detail-unverified-badge">Unverified email</span>}
              {p.email_verified && !p.disabled && p.role !== 'superadmin' && <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-[#EDF5EE] text-[#3A7D44]">Verified</span>}
            </div>
            <p className="text-sm text-[#5C6B63]">{p.email} · {p.phone || 'no phone'} · joined {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</p>
            <p className="text-xs text-[#5C6B63] font-mono mt-1">Workspace: {p.workspace_id}</p>
          </div>
          {p.role !== 'superadmin' && (
            <div className="flex gap-2 flex-wrap">
              {!p.email_verified && (
                <button onClick={onMarkVerified} className="rounded-md px-3 py-2 text-sm font-medium border border-[#3A7D44] text-[#3A7D44] hover:bg-[#EDF5EE]" data-testid="detail-mark-verified">Mark as verified</button>
              )}
              <button onClick={onToggleDisable} className={`rounded-md px-3 py-2 text-sm font-medium border ${p.disabled ? 'border-[#3A7D44] text-[#3A7D44] hover:bg-[#EDF5EE]' : 'border-[#8A6A00] text-[#8A6A00] hover:bg-[#FFF7E5]'}`} data-testid="detail-toggle-disable">{p.disabled ? 'Re-enable user' : 'Disable temporarily'}</button>
              <button onClick={onPermaDelete} className="rounded-md px-3 py-2 text-sm font-medium bg-[#D05A4A] hover:bg-[#B5483A] text-white" data-testid="detail-delete-btn">Delete permanently</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <Pill label="Bags (workspace)" value={detail.bags?.length || 0} />
          <Pill label="Sales logged" value={detail.sales?.length || 0} />
          <Pill label="Harvests logged" value={detail.harvests?.length || 0} />
          <Pill label="Expenses logged" value={detail.expenses?.length || 0} />
          <Pill label="Transfers" value={detail.transfers?.length || 0} />
        </div>

        {detail.workspace_totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="workspace-totals">
            <Pill label="Total Yield (kg)" value={detail.workspace_totals.total_yield_kg?.toFixed?.(2) ?? detail.workspace_totals.total_yield_kg ?? 0} tone="green" />
            <Pill label="Total Harvests" value={detail.workspace_totals.total_harvest_count || 0} tone="green" />
            <Pill label="Total Sales (₹)" value={`₹${Number(detail.workspace_totals.total_sales_amount || 0).toFixed(0)}`} tone="green" />
            <Pill label="Total Expenses (₹)" value={`₹${Number(detail.workspace_totals.total_expenses_amount || 0).toFixed(0)}`} tone="red" />
          </div>
        )}
      </div>

      <DataTable title="Bags created" rows={detail.bags} cols={['bag_id', 'variety', 'date_spawning', 'total_yield', 'status']} dateCols={['date_spawning']} />
      <DataTable title="Harvests logged" rows={detail.harvests} cols={['bag_id', 'harvest_date', 'quantity', 'flush_number']} dateCols={['harvest_date']} />
      <DataTable title="Sales logged" rows={detail.sales} cols={['sale_date', 'party_name', 'num_packets', 'total_amount', 'payment_type', 'amount_paid']} dateCols={['sale_date']} currencyCols={['total_amount', 'amount_paid']} />
      <DataTable title="Expenses logged" rows={detail.expenses} cols={['expense_date', 'expense_name', 'expense_amount', 'narration']} dateCols={['expense_date']} currencyCols={['expense_amount']} />
      <DataTable
        title="Parties created"
        rows={detail.parties}
        cols={['name', 'phone', 'quantity_per_packet', 'price_per_packet', 'created_at']}
        dateCols={['created_at']}
        currencyCols={['price_per_packet']}
        onRowClick={(r) => r.id && onPartyClick && onPartyClick(r.id)}
        clickHint="Click a party to view contact details"
      />
      <DataTable title="Money transfers" rows={detail.transfers} cols={['created_at', 'from_user_name', 'to_user_name', 'amount', 'note']} dateCols={['created_at']} currencyCols={['amount']} />
    </div>
  );
};

const Pill = ({ label, value, tone }) => {
  const toneCls = tone === 'green' ? 'bg-[#EDF5EE]' : tone === 'red' ? 'bg-[#FAEDED]' : 'bg-[#F7F5F0]';
  const valueCls = tone === 'green' ? 'text-[#3A7D44]' : tone === 'red' ? 'text-[#D05A4A]' : 'text-[#1A2620]';
  return (
    <div className={`p-3 rounded-lg ${toneCls}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B63]">{label}</p>
      <p className={`text-lg font-bold font-['IBM_Plex_Sans'] ${valueCls}`}>{value}</p>
    </div>
  );
};

const DataTable = ({ title, rows, cols, dateCols = [], currencyCols = [], onRowClick = null, clickHint = '' }) => (
  <div className="bg-white border border-[#E4DFD3] rounded-lg overflow-hidden">
    <div className="p-4 border-b border-[#E4DFD3] flex items-center justify-between flex-wrap gap-2">
      <h3 className="font-bold text-[#1A2620]">{title} ({rows?.length || 0})</h3>
      {clickHint && rows?.length > 0 && <span className="text-xs text-[#5C6B63] italic">{clickHint}</span>}
    </div>
    {!rows?.length ? (
      <div className="p-6 text-center text-sm text-[#5C6B63]">No records.</div>
    ) : (
      <div className="overflow-x-auto max-h-64 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0"><tr className="bg-[#F7F5F0] border-b border-[#E4DFD3] text-left text-xs font-semibold uppercase tracking-wider text-[#5C6B63]">
            {cols.map(c => <th key={c} className="p-3">{c.replace(/_/g, ' ')}</th>)}
          </tr></thead>
          <tbody>
            {rows.slice(0, 50).map((r, i) => (
              <tr
                key={r.id || i}
                className={`border-b border-[#E4DFD3] last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-[#EDF5EE]' : ''}`}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                data-testid={onRowClick && r.id ? `master-party-row-${r.id}` : undefined}
              >
                {cols.map(c => {
                  const v = r[c];
                  let display = v ?? '—';
                  if (dateCols.includes(c) && v) display = new Date(v).toLocaleString();
                  else if (currencyCols.includes(c) && v != null) display = `Rs.${Number(v).toFixed(0)}`;
                  return <td key={c} className="p-3 text-sm text-[#1A2620] font-['IBM_Plex_Sans']">{display}</td>;
                })}
              </tr>
            ))}
            {rows.length > 50 && <tr><td colSpan={cols.length} className="p-3 text-center text-xs text-[#5C6B63]">… {rows.length - 50} more rows hidden</td></tr>}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default Master;
